import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import './ScanTable.css';

const ScanTable = () => {
  const navigate = useNavigate();
  const [tableNumber, setTableNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  useEffect(() => {
    // Check if table ID in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const tableId = urlParams.get('table');
    if (tableId) {
      verifyAndNavigate(tableId);
    }
  }, []);

  const verifyAndNavigate = async (tableId) => {
    setLoading(true);
    setError('');
    
    try {
      const q = query(collection(db, 'tables'), where('tableNumber', '==', tableId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setError(`Table ${tableId} not found. Please check the table number.`);
        setLoading(false);
        return;
      }

      const tableDoc = snapshot.docs[0];
      const tableData = tableDoc.data();

      if (tableData.status === 'cleaning') {
        setError('This table is currently being cleaned. Please wait a moment.');
        setLoading(false);
        return;
      }

      // Navigate to menu for this table
      navigate(`/qr/table/${tableDoc.id}`);
    } catch (err) {
      console.error('Error verifying table:', err);
      setError('Failed to verify table. Please try again.');
      setLoading(false);
    }
  };

  const handleManualEntry = () => {
    if (!tableNumber.trim()) {
      setError('Please enter a table number');
      return;
    }
    verifyAndNavigate(tableNumber.trim());
  };

  const handleScanQR = () => {
    // In a real implementation, this would use camera API to scan QR codes
    // For now, show manual entry
    setShowManualEntry(true);
  };

  return (
    <div className="scan-table-page">
      <div className="scan-container">
        <div className="brand-header">
          <h1>🍽️ Odisha Zaika</h1>
          <p>Scan & Order</p>
        </div>

        {!showManualEntry ? (
          <div className="scan-section">
            <div className="qr-scanner-placeholder">
              <div className="scanner-icon">📷</div>
              <h2>Scan QR Code</h2>
              <p>Point your camera at the QR code on your table</p>
            </div>
            
            <button className="btn-scan" onClick={handleScanQR}>
              Open Camera to Scan
            </button>

            <div className="divider">
              <span>OR</span>
            </div>

            <button 
              className="btn-manual"
              onClick={() => setShowManualEntry(true)}
            >
              Enter Table Number Manually
            </button>
          </div>
        ) : (
          <div className="manual-entry-section">
            <div className="back-btn" onClick={() => setShowManualEntry(false)}>
              ← Back to Scanner
            </div>
            
            <h2>Enter Table Number</h2>
            <p>Find the table number on your table</p>

            <div className="input-group">
              <input
                type="text"
                placeholder="e.g., T1, A5, 101"
                value={tableNumber}
                onChange={(e) => {
                  setTableNumber(e.target.value);
                  setError('');
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleManualEntry()}
                autoFocus
                className={error ? 'error' : ''}
              />
              {error && <p className="error-message">{error}</p>}
            </div>

            <button 
              className="btn-continue"
              onClick={handleManualEntry}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span> Verifying...
                </>
              ) : (
                'Continue to Menu'
              )}
            </button>

            <div className="help-section">
              <h3>Need Help?</h3>
              <ul>
                <li>Look for a sticker or card on your table with the table number</li>
                <li>Table numbers are usually in format like "T1", "A5", or "101"</li>
                <li>Ask a staff member if you can't find your table number</li>
              </ul>
            </div>
          </div>
        )}

        <div className="features-section">
          <h3>Why Order via QR?</h3>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h4>Fast & Easy</h4>
              <p>Browse menu and order instantly</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h4>Contactless</h4>
              <p>Safe and hygienic ordering</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💳</div>
              <h4>Multiple Payments</h4>
              <p>UPI, Card, or Cash</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📍</div>
              <h4>Track Order</h4>
              <p>Real-time order status</p>
            </div>
          </div>
        </div>

        <div className="footer-info">
          <p>🍴 Dine-in • Table Service</p>
          <p className="restaurant-info">
            <a href="/menu">View Full Menu</a> • 
            <a href="/contact">Contact Us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScanTable;
